import React, { useCallback, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useCurrentUser } from '../../../../context/userContext'
import Container from '../../../atoms/page/Page'
import Input from '../../../atoms/form/input/Input'
import InlineGroup from '../../../atoms/inlinegroup/InlineGroup'
import LoadingSpinner from '../../../atoms/loadingspinner/LoadingSpinner'
import ParticipantsSection from '../participants/ParticipantsSection'
import { fullDate } from '../../../../lib/date'
import DeleteEvent from './DeleteEvent'
import ToggleEvent from './ToggleEvent'
import openNewTab from '../../../../lib/openNewTab'
import DisplayField from '../../../atoms/form/display-field/DisplayField'
import Form from '../../../atoms/form/Form'
import Banner from '../../../atoms/banner/Banner'
import IconButton from '../../../molecules/iconbutton/IconButton'
import VerticalGroup from '../../../atoms/verticalgroup/VerticalGroup'
import SelectBranding from './SelectBranding'
import SelectSimulation from './SelectSimulation'
import EventSchedulingSummary from '../event-scheduling/EventSchedulingSummary'
import RoleRequired from '../../../molecules/role-required/RoleRequired'
import AuditLogsModal from '../../administration/audit/AuditLogsModal'
import SelectAssessment from './SelectAssessment'
import { useCurrentEvent } from '../../../../context/CurrentEventContext'

interface EventState {
  eventModalOpen: boolean
  deleteModalOpen: boolean
  auditModalOpen: boolean
}

function ViewEvent() {
  let { eventId } = useParams<{ eventId: string }>()
  const history = useHistory()

  const user = useCurrentUser()

  const { inProgress, event: data, error, refresh } = useCurrentEvent()

  const [{ eventModalOpen, deleteModalOpen, auditModalOpen }, setEventState] = useState<EventState>({
    eventModalOpen: false,
    deleteModalOpen: false,
    auditModalOpen: false,
  })

  const onEventToggleComplete = useCallback(() => {
    setEventState(prevState => ({ ...prevState, eventModalOpen: false }))
    refresh()
  }, [refresh])

  const onEventDeleteComplete = useCallback(() => {
    setEventState(prevState => ({ ...prevState, eventModalOpen: false }))
    history.push('/events')
  }, [history])

  return (
    <>
      {eventModalOpen && !!data && <ToggleEvent
        isOpen={eventModalOpen}
        onClose={() => setEventState(prevState => ({ ...prevState, eventModalOpen: false }))}
        onComplete={onEventToggleComplete}
        event={data}
      />}
      {deleteModalOpen && !!data && <DeleteEvent
        isOpen={deleteModalOpen}
        onClose={() => setEventState(prevState => ({ ...prevState, deleteModalOpen: false }))}
        onComplete={onEventDeleteComplete}
        event={data}
      />}
      {auditModalOpen && !!data && <AuditLogsModal
        isOpen={auditModalOpen}
        onClose={() => setEventState(prevState => ({ ...prevState, auditModalOpen: false }))}
        event={data}
      />}
      <Container className='event-edit'>
        <Banner type='error' active={!!error} message={error?.message} />
        {inProgress && <LoadingSpinner />}
        <InlineGroup spread verticalCenter>
          <h2>Event Details</h2>
          {!!data && !inProgress && <InlineGroup spread spaceLeft={2}>
            <RoleRequired roles={['superadmin']}>
              <IconButton data-test={`model-event-${eventId}`} icon='audit' text='Audit Logs' onClick={() => setEventState(prevState => ({ ...prevState, auditModalOpen: true }))} />
              <IconButton data-test={`model-event-${eventId}`} icon='preview' text='Debug Model' linkTo={`/events/${eventId}/debug`} />
            </RoleRequired>
            <RoleRequired roles={['superadmin']}>
              <IconButton data-test={`edit-event-${eventId}-v2`} icon='edit' text='Switch UI' linkTo={`/events/${eventId}/view-wizard`} />
            </RoleRequired>
            <IconButton data-test={`edit-event-${eventId}`} icon='edit' text='Edit Event' linkTo={`/events/${eventId}/edit`} />
            <IconButton data-test={`clone-event-${eventId}`} icon='clone' text='Clone Event' linkTo={`/events/${eventId}/clone`} />
            <IconButton data-test={`cheatsheet-${data.id}`} icon='idea' text='Open Cheatsheet' onClick={() => openNewTab(`${process.env.REACT_APP_GAME_API_URL}/admin?gameId=${eventId}&authToken=${user && user.token}`)} />
            <IconButton data-test={`toggle-${data.id}`} icon={data.closed ? 'lockOpen' : 'lockClosed'} text={data.closed ? 'Open Event' : 'Close Event'} onClick={() => setEventState(prevState => ({ ...prevState, eventModalOpen: true }))} />
            <IconButton danger data-test={`delete-${data.id}`} icon='trash' text='Delete' onClick={() => setEventState(prevState => ({ ...prevState, deleteModalOpen: true }))} />
          </InlineGroup>}
        </InlineGroup>


        {!!data && !inProgress && (
          <>
            <Form className='event-details'>
              <Input data-test='date' type='text' value={fullDate(data.date)} label='Event Date' readOnly />
              <Input data-test='client' type='text' value={data.client} label='Client' readOnly />
              <Input data-test='name' type='text' value={data.name} label='Event Name' readOnly />
              <Input data-test='streamName' type='text' value={data.streamName || ''} label='Stream Name' readOnly />
              <Input data-test='location' type='text' value={data.location} label='Location' readOnly />
              <DisplayField label='Event ID' value={data.id} />
              <DisplayField label='Participants #' value={data.participants.length} />
            </Form>
          </>
        )}
        <VerticalGroup spaceBetweenElements={2}>
          <h2>Simulation Settings</h2>
          {data && <SelectBranding branding={data.branding} brandingId={data.brandingId} clientId={data.id} readOnly />}
          {data && <SelectSimulation clientId={data.clientId} simulationId={data.simulationId} readOnly />}
          {data && <EventSchedulingSummary readOnly schedule={data.schedule} />}
          {data && <SelectAssessment clientId={data.clientId} assessmentId={data.assessmentId} readOnly />}
        </VerticalGroup>
        <hr />
        {!!data && !inProgress && <ParticipantsSection eventId={data.id} participants={data.participants} refreshEvent={refresh} data={data} />}

      </Container>
    </>
  )
}

export default ViewEvent
